import {
  BookedParty,
  PartyData,
  PartyExtra,
  PartySession,
  TimeSlot,
} from '@types';
import {
  AdditionalDetails,
  ChoosePartyExtras,
  CustomerInfo,
  PartySelectSessionDateTime,
  SelectPartyPackage,
} from '_molecules';
import AdminService from '_services/AdminService';
import PartyService from '_services/PartyService';
import { HelperMethods } from '_utils';
import React, { useEffect, useState } from 'react';
import { MdClose, MdEdit } from 'react-icons/md';

interface EditPartyModalProps {
  isOpen: boolean;
  onClose: () => void;
  party: BookedParty | null;
  onUpdate: () => void;
}

type EditStep =
  | 'selectPartyPackage'
  | 'bookDate'
  | 'chooseExtras'
  | 'additionalDetails'
  | 'customerInfo'
  | 'payment'
  | 'confirmation'
  | 'overview';

export const EditPartyModal: React.FC<EditPartyModalProps> = ({
  isOpen,
  onClose,
  party,
  onUpdate,
}) => {
  const [currentStep, setCurrentStep] = useState<EditStep>('overview');
  const [partyData, setPartyData] = useState<PartyData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);

  // Convert BookedParty to PartyData format
  useEffect(() => {
    if (party && isOpen) {
      // Reset sendEmail when opening modal
      setSendEmail(false);
      const convertToPartyData = async () => {
        setIsLoading(true);
        try {
          // Load party sessions and extras
          const [sessionsData, extrasData] = await Promise.all([
            PartyService.getPariesList(),
            PartyService.getPartyExtras(),
          ]);

          const sessions = (sessionsData as unknown as PartySession[]) || [];
          const extras = (extrasData as unknown as PartyExtra[]) || [];

          // Find the matching party session
          const matchingSession = sessions.find(
            s => s.name === party.name || s.id === party.partyId.toString(),
          );

          // Convert booked extras to ExtraSelection format
          const selectedExtras =
            party.bookedPartyExtras?.map(bookedExtra => {
              const extra = extras.find(e => e.name === bookedExtra.name);
              return {
                extra: extra || {
                  id: bookedExtra.id,
                  type: bookedExtra.type,
                  name: bookedExtra.name,
                  price: bookedExtra.price,
                  description: bookedExtra.description || '',
                  photoUrl: '',
                },
                quantity: bookedExtra.quantity || 0,
              };
            }) || [];

          // Parse pizza list if available
          const selectedPizzas: any[] = [];
          if (party.pizzaList) {
            // Pizza list is stored as a string, need to parse it
            // This is a simplified parser - adjust based on actual format
            const pizzaMatches = party.pizzaList.match(/(\d+)\s*x\s*([^,]+)/g);
            if (pizzaMatches) {
              pizzaMatches.forEach(match => {
                const parts = match.match(/(\d+)\s*x\s*(.+)/);
                if (parts && matchingSession?.partyPizzas) {
                  const quantity = parseInt(parts[1]);
                  const pizzaName = parts[2].trim();
                  const pizza = matchingSession.partyPizzas.find(
                    p => p.name === pizzaName,
                  );
                  if (pizza) {
                    selectedPizzas.push({ pizza, quantity });
                  }
                }
              });
            }
          }

          // Create TimeSlot from sessionDate and timeSlot
          // Ensure date is in YYYY-MM-DD format (local timezone)
          const formattedDate = party.sessionDate
            ? (() => {
                const date = new Date(party.sessionDate);
                const yyyy = date.getFullYear();
                const mm = String(date.getMonth() + 1).padStart(2, '0');
                const dd = String(date.getDate()).padStart(2, '0');
                return `${yyyy}-${mm}-${dd}`;
              })()
            : '';
          const timeSlot: TimeSlot = {
            id: '',
            date: formattedDate,
            timeSlot: party.timeSlot,
            maxCapacity: 0,
            originalMaxCapacity: 0,
            isActive: true,
            adultPrice: 0,
            childPrice: 0,
            partiesLimit: 0,
            custom15PackDiscountPercentage: 0,
            isLinkOnly: false,
            linkOnlyUrlCode: '',
          };

          const convertedData: PartyData = {
            barcodeId: party.barcodeId,
            selectedPartyPackage: matchingSession,
            selectedTimeSlot: timeSlot,
            selectedExtras,
            selectedPizzas,
            additionalDetails: {
              guestName: party.guestName || '',
              notes: party.notes || '',
            },
            customerInfo: {
              id: party.customerId.toString(),
              firstName: party.customerFirstName,
              lastName: party.customerLastName,
              email: party.customerEmail,
              phoneNumber: party.customerPhone,
            },
          };

          setPartyData(convertedData);
        } catch (error) {
          console.error('Error converting party data:', error);
        } finally {
          setIsLoading(false);
        }
      };

      convertToPartyData();
    }
  }, [party, isOpen]);

  const handleStepChange = (step: EditStep) => {
    setCurrentStep(step);
  };

  const handleStepComplete = (data: Partial<PartyData>) => {
    if (partyData) {
      setPartyData({ ...partyData, ...data });
    }
  };

  const handleSave = async () => {
    if (!party || !partyData) return;

    setIsSaving(true);
    try {
      // If timeSlot was changed, we need to fetch the actual TimeSlot to get its ID
      let timeSlotId = partyData.selectedTimeSlot?.id;
      if (
        partyData.selectedTimeSlot &&
        (!timeSlotId || timeSlotId === '') &&
        partyData.selectedTimeSlot.date
      ) {
        try {
          const { TicketService } = await import('_services');
          const timeSlots = (await TicketService.getTimeSlotsForDate(
            partyData.selectedTimeSlot.date,
          )) as unknown as TimeSlot[];
          const matchingTimeSlot = timeSlots.find(
            ts =>
              ts.date === partyData.selectedTimeSlot?.date &&
              ts.timeSlot === partyData.selectedTimeSlot?.timeSlot,
          );
          if (matchingTimeSlot) {
            timeSlotId = matchingTimeSlot.id;
          }
        } catch (error) {
          console.error('Error fetching time slot:', error);
        }
      }

      // Prepare update payload
      const updatePayload = {
        selectedPartyPackage: partyData.selectedPartyPackage,
        timeSlotId: parseInt(
          timeSlotId || partyData.selectedTimeSlot?.id || '0',
        ),
        selectedTimeSlotId: parseInt(partyData.selectedTimeSlot?.id || '0'),
        sessionDate: partyData.selectedTimeSlot?.date,
        timeSlot: partyData.selectedTimeSlot?.timeSlot,
        updatedExtras: partyData.selectedExtras.map(e => ({
          extraId: e.extra.id,
          quantity: e.quantity,
          type: e.extra.type,
          name: e.extra.name,
        })),
        updatedPizzas: partyData.selectedPizzas.map(p => ({
          pizzaId: p.pizza.id,
          quantity: p.quantity,
          name: p.pizza.name,
        })),
        pizzaList: party.pizzaList || '',
        guestName: partyData.additionalDetails?.guestName || '',
        notes: partyData.additionalDetails?.notes || '',
        customerInfo: partyData.customerInfo,
        barcodeId: partyData.barcodeId,
        sendEmail,
      };

      // Call update API
      await AdminService.updateBookedParty(party.id, updatePayload);
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating party:', error);
      alert('Failed to update party. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStepBack = () => {
    setCurrentStep('overview');
  };

  const formatValue = (value: any): string => {
    if (!value) return 'Not set';
    if (typeof value === 'object') {
      if (value.name) return value.name;
      if (value.date) {
        return `${HelperMethods.formatDate(value.date)} ${HelperMethods.formatTime12Hour(value.timeSlot || '')}`;
      }
      return JSON.stringify(value);
    }
    return String(value);
  };

  if (!isOpen || !party) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">
                {currentStep === 'overview'
                  ? 'Edit Party'
                  : `Change ${currentStep}`}
              </h3>
              {currentStep === 'overview' ? (
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <MdClose size={24} />
                </button>
              ) : (
                <button
                  onClick={handleStepBack}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Back to Overview
                </button>
              )}
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : currentStep === 'overview' ? (
              <div className="space-y-4">
                {/* Party Package */}
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <div className="text-sm text-gray-500 mb-1">
                      Party Package
                    </div>
                    <div className="text-base font-medium text-gray-900">
                      {formatValue(partyData?.selectedPartyPackage)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleStepChange('selectPartyPackage')}
                    className="ml-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <MdEdit className="mr-2" />
                    Change
                  </button>
                </div>

                {/* Date & Time */}
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <div className="text-sm text-gray-500 mb-1">
                      Date & Time
                    </div>
                    <div className="text-base font-medium text-gray-900">
                      {partyData?.selectedTimeSlot
                        ? `${partyData.selectedTimeSlot.date} ${HelperMethods.formatTime12Hour(partyData.selectedTimeSlot.timeSlot)}`
                        : formatValue(partyData?.selectedTimeSlot)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleStepChange('bookDate')}
                    className="ml-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <MdEdit className="mr-2" />
                    Change
                  </button>
                </div>

                {/* Extras */}
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <div className="text-sm text-gray-500 mb-1">Extras</div>
                    <div className="text-base font-medium text-gray-900">
                      {partyData?.selectedExtras &&
                      partyData.selectedExtras.length > 0
                        ? partyData.selectedExtras
                            .map(e => `${e.extra.name} (x${e.quantity})`)
                            .join(', ')
                        : 'No extras'}
                    </div>
                  </div>
                  <button
                    onClick={() => handleStepChange('chooseExtras')}
                    className="ml-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <MdEdit className="mr-2" />
                    Change
                  </button>
                </div>

                {/* Additional Details */}
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <div className="text-sm text-gray-500 mb-1">
                      Additional Details
                    </div>
                    <div className="text-base font-medium text-gray-900">
                      {partyData?.additionalDetails?.guestName ||
                      partyData?.additionalDetails?.notes
                        ? `Guest: ${partyData.additionalDetails.guestName || 'N/A'}, Cust. Notes: ${partyData.additionalDetails.notes || 'N/A'}`
                        : 'No additional details'}
                    </div>
                  </div>
                  <button
                    onClick={() => handleStepChange('additionalDetails')}
                    className="ml-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <MdEdit className="mr-2" />
                    Change
                  </button>
                </div>

                {/* Customer Info */}
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <div className="text-sm text-gray-500 mb-1">
                      Customer Info
                    </div>
                    <div className="text-base font-medium text-gray-900">
                      {partyData?.customerInfo
                        ? `${partyData.customerInfo.firstName} ${partyData.customerInfo.lastName} (${partyData.customerInfo.email})`
                        : 'No customer info'}
                    </div>
                  </div>
                  <button
                    onClick={() => handleStepChange('customerInfo')}
                    className="ml-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <MdEdit className="mr-2" />
                    Change
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="pt-4 border-t border-gray-200 space-y-4">
                  {/* Send Email Checkbox */}
                  <div className="flex items-center">
                    <input
                      id="sendEmail"
                      type="checkbox"
                      checked={sendEmail}
                      onChange={e => setSendEmail(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                    />
                    <label
                      htmlFor="sendEmail"
                      className="ml-2 text-sm text-gray-700 cursor-pointer"
                    >
                      Send updated email to customer
                    </label>
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Step Content */}
                <div className="bg-gray-50 rounded-lg p-4">
                  {currentStep === 'selectPartyPackage' && partyData && (
                    <SelectPartyPackage
                      selectedPartyPackage={partyData.selectedPartyPackage}
                      selectedPizzas={partyData.selectedPizzas}
                      onNext={data => {
                        if (data) handleStepComplete(data);
                        handleStepBack();
                      }}
                      onPrevious={handleStepBack}
                      onStepComplete={handleStepComplete}
                    />
                  )}

                  {currentStep === 'bookDate' && partyData && (
                    <PartySelectSessionDateTime
                      selectedTimeSlot={partyData.selectedTimeSlot}
                      onNext={data => {
                        if (data) handleStepComplete(data);
                        handleStepBack();
                      }}
                      onPrevious={handleStepBack}
                      onStepComplete={handleStepComplete}
                    />
                  )}

                  {currentStep === 'chooseExtras' && partyData && (
                    <ChoosePartyExtras
                      selectedExtras={partyData.selectedExtras}
                      onNext={data => {
                        if (data) handleStepComplete(data);
                        handleStepBack();
                      }}
                      onPrevious={handleStepBack}
                      onStepComplete={handleStepComplete}
                    />
                  )}

                  {currentStep === 'additionalDetails' && partyData && (
                    <AdditionalDetails
                      additionalDetails={partyData.additionalDetails}
                      notesReadOnly
                      onNext={data => {
                        if (data) handleStepComplete(data);
                        handleStepBack();
                      }}
                      onPrevious={handleStepBack}
                      onStepComplete={handleStepComplete}
                    />
                  )}

                  {currentStep === 'customerInfo' && partyData && (
                    <CustomerInfo
                      customerInfo={partyData.customerInfo}
                      onNext={data => {
                        if (data) handleStepComplete(data);
                        handleStepBack();
                      }}
                      onPrevious={handleStepBack}
                      onStepComplete={handleStepComplete}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
